import { useState, useEffect, useCallback } from "react";
import { Link, Navigate } from "react-router-dom";
import Navbar from "../../../components/feature/Navbar";
import Footer from "../../../components/feature/Footer";
import ProviderStats from "./components/ProviderStats";
import ProviderProducts from "./components/ProviderProducts";
import ProviderPlan from "./components/ProviderPlan";
import ProviderAnalytics from "./components/ProviderAnalytics";
import ProviderCategories from "./components/ProviderCategories";
import ProviderLTV from "./components/ProviderLTV";
import ProviderCoupons from "./components/ProviderCoupons";
import ProviderProfileScore from "./components/ProviderProfileScore";
import { useApp } from "../../../contexts/AppContext";
import { supabase } from "../../../lib/supabase";

type Tab = "dashboard" | "analytics" | "products" | "ltv" | "coupons" | "categories" | "plan";

const tabs: { id: Tab; label: string; icon: string }[] = [
  { id: "dashboard", label: "ダッシュボード", icon: "ri-dashboard-line" },
  { id: "analytics", label: "売上・分析", icon: "ri-bar-chart-2-line" },
  { id: "products", label: "商品管理", icon: "ri-store-2-line" },
  { id: "ltv", label: "ファン分析", icon: "ri-user-star-line" },
  { id: "coupons", label: "クーポン", icon: "ri-coupon-line" },
  { id: "categories", label: "カテゴリ", icon: "ri-folder-3-line" },
  { id: "plan", label: "プラン管理", icon: "ri-vip-crown-line" },
];

interface ProviderHeaderStats {
  monthSales: number;
  followerCount: number;
  productCount: number;
  avgRating: number;
}

export default function ProviderMypagePage() {
  const [activeTab, setActiveTab] = useState<Tab>("dashboard");
  const { currentUser, isLoggedIn, authLoading } = useApp();
  const [headerStats, setHeaderStats] = useState<ProviderHeaderStats>({ monthSales: 0, followerCount: 0, productCount: 0, avgRating: 0 });

  const loadHeaderStats = useCallback(async () => {
    if (!currentUser?.id) return;
    const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString();
    const [
      { data: salesData },
      { count: followerCount },
      { count: productCount },
      { data: reviewData },
    ] = await Promise.all([
      supabase.from("purchases").select("price").eq("provider_id", currentUser.id).eq("status", "completed").gte("purchased_at", monthStart),
      supabase.from("follows").select("id", { count: "exact", head: true }).eq("provider_id", currentUser.id),
      supabase.from("products").select("id", { count: "exact", head: true }).eq("provider_id", currentUser.id).eq("visibility", "public"),
      supabase.from("reviews").select("rating").eq("provider_id", currentUser.id).eq("is_hidden", false),
    ]);
    const monthSales = (salesData || []).reduce((s, p: { price: number }) => s + p.price, 0);
    const ratings = (reviewData || []).map((r: { rating: number }) => r.rating);
    const avgRating = ratings.length > 0 ? ratings.reduce((a, b) => a + b, 0) / ratings.length : 0;
    setHeaderStats({ monthSales, followerCount: followerCount ?? 0, productCount: productCount ?? 0, avgRating });
  }, [currentUser?.id]);

  useEffect(() => { loadHeaderStats(); }, [loadHeaderStats]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#0f1f2e" }}>
        <Navbar />
        <i className="ri-loader-4-line animate-spin text-3xl text-white/60" />
      </div>
    );
  }

  if (!isLoggedIn) return <Navigate to="/login" replace />;

  const displayService = currentUser?.serviceName || currentUser?.name || "あなたのショップ";
  const displayName = currentUser?.name || "事業者";
  const planLabel = currentUser?.plan === "light" ? "ライトプラン" : "フリープラン";

  return (
    <div className="min-h-screen" style={{ background: "#0f1f2e" }}>
      <Navbar />
      <main className="pt-20">
        <section className="px-4 py-10" style={{ background: "linear-gradient(135deg, #0f1f2e 0%, #1a3a52 100%)" }}>
          <div className="max-w-screen-xl mx-auto">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
              <div className="w-20 h-20 rounded-2xl flex-shrink-0 border-2 border-white/20 bg-white/10 flex items-center justify-center">
                <i className="ri-store-2-line text-3xl text-white/60" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-1 flex-wrap">
                  <p className="font-display italic text-amber-400 text-xs tracking-widest">Provider Account</p>
                  <span className="text-xs bg-white/10 text-white/60 px-2 py-0.5 rounded-full font-sans">{planLabel}</span>
                </div>
                <h1 className="font-serif text-white text-2xl font-light">{displayService}</h1>
                <p className="text-white/40 text-sm font-sans mt-1">{displayName}</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
                <Link to="/sell" className="text-sm bg-amber-500 text-white px-6 py-2.5 rounded-full font-sans cursor-pointer hover:bg-amber-400 transition-colors whitespace-nowrap text-center">
                  <i className="ri-add-line mr-1" />新規出品
                </Link>
                <Link to="/mypage" className="text-sm border border-white/30 text-white/80 px-6 py-2.5 rounded-full font-sans cursor-pointer hover:bg-white/10 transition-colors whitespace-nowrap text-center">
                  <i className="ri-user-3-line mr-1" />受講者モードへ
                </Link>
              </div>
            </div>
            <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: "今月売上", value: `¥${headerStats.monthSales.toLocaleString()}`, icon: "ri-money-yen-circle-line" },
                { label: "フォロワー", value: headerStats.followerCount.toLocaleString(), icon: "ri-user-follow-line" },
                { label: "出品数", value: `${headerStats.productCount}点`, icon: "ri-store-2-line" },
                { label: "平均評価", value: headerStats.avgRating > 0 ? headerStats.avgRating.toFixed(1) : "—", icon: "ri-star-fill" },
              ].map((s) => (
                <div key={s.label} className="rounded-2xl p-4" style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)" }}>
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className={`${s.icon} text-amber-400 text-xl`} />
                  </div>
                  <p className="font-serif text-white text-2xl mt-2">{s.value}</p>
                  <p className="text-white/40 text-xs font-sans mt-0.5">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 sticky top-16 z-30" style={{ background: "#162334", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
          <div className="max-w-screen-xl mx-auto flex gap-1 overflow-x-auto">
            {tabs.map((t) => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={`flex items-center gap-2 px-4 py-4 text-xs font-sans whitespace-nowrap cursor-pointer border-b-2 transition-all duration-200 ${activeTab === t.id ? "border-amber-400 text-amber-400" : "border-transparent text-white/40 hover:text-white/70"}`}
              >
                <i className={t.icon} />
                <span className="hidden sm:inline">{t.label}</span>
              </button>
            ))}
          </div>
        </section>

        <section className="px-4 py-8" style={{ background: "#f9f8f5", minHeight: "50vh" }}>
          <div className="max-w-screen-xl mx-auto space-y-6">
            {activeTab === "dashboard" && (
              <>
                <ProviderProfileScore />
                <ProviderStats onStatsUpdated={loadHeaderStats} />
              </>
            )}
            {activeTab === "analytics" && <ProviderAnalytics />}
            {activeTab === "products" && <ProviderProducts />}
            {activeTab === "ltv" && <ProviderLTV />}
            {activeTab === "coupons" && <ProviderCoupons />}
            {activeTab === "categories" && <ProviderCategories />}
            {activeTab === "plan" && <ProviderPlan />}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
